import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getUser } from "../../api/getUser";
import Loading from "../common/Loading";
import { FaEdit } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { httpReauest } from "../../utils/httpRequest";

const UserCard = ({ data, ref }) => {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Validation schema for step 1 (email edit)
  const emailEditSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
  });

  // Validation schema for step 2 (email approval)
  const emailApproveSchema = Yup.object().shape({
    code: Yup.string()
      .required("Verification code is required")
      .min(6, "Code must be at least 6 characters"),
  });

  // Formik for step 1 (email edit)
  const emailEditFormik = useFormik({
    initialValues: {
      email: data?.email || "",
    },
    validationSchema: emailEditSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem("user"));

        await httpReauest(
          "POST",
          "/user/edit/email",
          { email: values.email },
          { "x-access-token": user.token }
        );
        toast.success("Verification code sent to your email");
        setStep(2);
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to update email address"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  // Formik for step 2 (email approval)
  const emailApproveFormik = useFormik({
    initialValues: {
      code: "",
    },
    validationSchema: emailApproveSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem("user"));
        await httpReauest(
          "POST",
          "/user/approve/email",
          { email: emailEditFormik.values.email, code: values.code },
          { "x-access-token": user.token }
        );
        toast.success("Email address updated successfully");
        setShow(false);
        setStep(1);
        // You might want to refresh user data here
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to verify email address"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  // Reset forms when modal is closed
  useEffect(() => {
    if (!show) {
      setStep(1);
      emailEditFormik.resetForm();
      emailApproveFormik.resetForm();
    }
  }, [show]);

  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date)) {
      throw new Error("Invalid date provided");
    }

    const day = date.getDate();
    const month = date
      .toLocaleString("default", { month: "short" })
      .toUpperCase();
    const year = date.getFullYear();

    return `${day} ${month}, ${year}`;
  };

  return (
    <div
      ref={ref}
      className="rounded-[16px] bg-[#0E171D] p-4 lg:px-6 lg:py-3 gap-5 flex lg:flex-row flex-col w-full justify-between items-center relative"
    >
      {show && (
        <>
          <div
            className="fixed left-0 top-0 w-full h-full bg-black/70 z-40"
            onClick={() => setShow(false)}
          />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-700 rounded-[16px] p-5 z-50 w-[320px]">
            {step === 1 ? (
              <form onSubmit={emailEditFormik.handleSubmit}>
                <h3 className="text-white text-center text-[22px] font-bold">
                  Edit Email
                </h3>
                <div className="mt-3">
                  <label className="text-[14px] text-white">Email</label>
                  <input
                    name="email"
                    value={emailEditFormik.values.email}
                    onChange={emailEditFormik.handleChange}
                    onBlur={emailEditFormik.handleBlur}
                    className="form-brand w-full text-black mt-1"
                    placeholder="Enter Email"
                  />
                  {emailEditFormik.touched.email &&
                    emailEditFormik.errors.email && (
                      <div className="text-red-500 text-xs mt-1">
                        {emailEditFormik.errors.email}
                      </div>
                    )}
                </div>
                <div className="mt-4">
                  <button
                    type="submit"
                    className="btn-brand w-full"
                    disabled={loading}
                  >
                    {loading ? "Processing..." : "Submit"}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={emailApproveFormik.handleSubmit}>
                <h3 className="text-white text-center text-[22px] font-bold">
                  Approve Email
                </h3>
                <p className="text-[14px] text-white mt-2 mb-3">
                  We sent a code to your email
                </p>
                <div>
                  <label className="text-[14px] text-white">Code</label>
                  <input
                    name="code"
                    value={emailApproveFormik.values.code}
                    onChange={emailApproveFormik.handleChange}
                    onBlur={emailApproveFormik.handleBlur}
                    className="form-brand w-full text-black mt-1"
                    placeholder="Enter Verification Code"
                  />
                  {emailApproveFormik.touched.code &&
                    emailApproveFormik.errors.code && (
                      <div className="text-red-500 text-xs mt-1">
                        {emailApproveFormik.errors.code}
                      </div>
                    )}
                </div>
                <div className="mt-4">
                  <button
                    type="submit"
                    className="btn-brand w-full"
                    disabled={loading}
                  >
                    {loading ? "Verifying..." : "Verify"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </>
      )}
      {!data && (
        <div className="absolute left-0 top-0 z-40 w-full h-full flex  items-center justify-center bg-[#000]/30">
          <Loading />
        </div>
      )}
      <span className="flex w-full lg:w-fit justify-center lg:justify-start gap-4 items-center">
        {data && (
          <span className="relative">
            <button
              onClick={() => setShow(true)}
              className="text-white hover:text-yellow-500 duration-200 absolute top-0 left-full  text-[26px]"
            >
              <FaEdit className="" />
            </button>
            <img
              src={`/profile/${data?.gender?.toLowerCase()}/${data?.level > 6 ? 6 : data?.level}.jpg`}
              className="w-32"
              alt="User Profile"
            />
            {data?.loginStreak?.streak >= 90 ? (
              <img
                src="/streak/90.png"
                className="absolute right-0 bottom-8  w-12"
                alt="Strak"
              />
            ) : data?.loginStreak?.streak >= 60 ? (
              <img
                src="/streak/60.png"
                className="absolute right-0 bottom-8  w-12"
                alt="Strak"
              />
            ) : data?.loginStreak?.streak >= 30 ? (
              <img
                src="/streak/30.png"
                className="absolute right-0 bottom-8  w-12"
                alt="Strak"
              />
            ) : data?.loginStreak?.streak >= 14 ? (
              <img
                src="/streak/14.png"
                className="absolute right-0 bottom-8  w-12"
                alt="Strak"
              />
            ) : data?.loginStreak?.streak >= 7 ? (
              <img
                src="/streak/7.png"
                className="absolute right-0 bottom-8  w-12"
                alt="Strak"
              />
            ) : null}
          </span>
        )}

        <span>
          <h2 className="font-bold text-[32px]">{data?.name}</h2>
          <p className="text-18px]">
            Joined {data ? formatDate(new Date(data?.createdAt)) : null}
          </p>
        </span>
      </span>
      <span>
        <h4 className="text-[28px] text-center font-bold">TRUST POINT</h4>
        <h4 className="text-[36px] text-center font-bold">{data?.balance}</h4>
      </span>
    </div>
  );
};

export default UserCard;
